import { useCallback, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { CodeResponse, useGoogleLogin } from '@react-oauth/google'
import { PlaybackCountryCodeMap } from '@playback-rewards/shared-libraries'

//constants
import {
  LOCAL_STORAGE,
  REACT_NATIVE_STATUS,
  REQUIRED_PARSING_ANDROID_VERSION_CODE,
  USER_PLATFORM,
} from 'constants/index'
//contexts
import AuthContext from 'context/AuthContext'
import LoaderContext from 'context/LoaderContext'
import NativeAppContext from 'context/NativeApp/NativeAppContext'
//services
import { authorize, getAuthLinkWithScopes, signupWithGmail } from 'services/authService'
//utils
import { getUserPlatform, handleRNLogin, handleError, sendRNMessage } from 'utils/utils'
import { decodeToken } from 'react-jwt'
import useGeoDataQuery from './useGeoDataQuery'

const useAuthorization = (isBanned?: boolean, signUpParams?: string) => {
  const { login } = useContext(AuthContext)
  const { showLoader, hideLoader } = useContext(LoaderContext)
  const { nativeAppDetails } = useContext(NativeAppContext)
  const navigate = useNavigate()
  const { geoData } = useGeoDataQuery()

  const checkAccessByGeo = async (): Promise<{ ip: string; country: string | null } | null> => {
    if (geoData) {
      if (
        geoData.country_code2 &&
        !Object.keys(PlaybackCountryCodeMap).includes(geoData.country_code2)
      ) {
        navigate(`/country-error?detected-country=${geoData.country_code2}`)
        return null
      } else {
        return {
          ip: geoData.ip,
          country: geoData.country_code2,
        }
      }
    } else {
      return {
        ip: '',
        country: null,
      }
    }
  }

  const handleAuthorizationNative = useCallback(async () => {
    if (isBanned) {
      navigate('/blocked')
      return
    }

    const userGeoInfo = await checkAccessByGeo()
    if (!userGeoInfo) {
      return
    }
    return handleRNLogin(userGeoInfo)
  }, [navigate])

  const handleSignupNative = useCallback(async () => {
    if (isBanned) {
      navigate('/blocked')
      return
    }

    const userGeoInfo = await checkAccessByGeo()
    if (!userGeoInfo) {
      return
    }

    return Number(nativeAppDetails?.versionCode) >= REQUIRED_PARSING_ANDROID_VERSION_CODE
      ? sendRNMessage({
          status: REACT_NATIVE_STATUS.SIGNUP,
          userGeoInfo,
          referralId: sessionStorage.getItem(LOCAL_STORAGE.REFERRAL_ID) || null,
        })
      : sendRNMessage({
          status: REACT_NATIVE_STATUS.LOGIN,
          geoData: userGeoInfo,
          referralId: sessionStorage.getItem(LOCAL_STORAGE.REFERRAL_ID) || null,
        })
  }, [navigate])

  const handleGoogleLoginSuccess = async (
    codeResponse: CodeResponse,
    isBanned: boolean,
    isSignUp: boolean
  ) => {
    try {
      showLoader()

      if (isBanned) {
        hideLoader()
        navigate('/blocked')
        return
      }

      const userGeoInfo = await checkAccessByGeo()
      if (!userGeoInfo) {
        hideLoader()
        return
      }

      const authResp = isSignUp
        ? await signupWithGmail({
            platform: getUserPlatform(USER_PLATFORM.ANDROID),
            IPAddress: userGeoInfo.ip,
            authorizationCode: codeResponse.code,
            referrerId: sessionStorage.getItem(LOCAL_STORAGE.REFERRAL_ID) || null,
            signupAttr: signUpParams || null,
            country: userGeoInfo.country,
            signUpIPCountry: userGeoInfo.country,
            redirectUri: 'postmessage',
          })
        : await authorize({
            platform: getUserPlatform(USER_PLATFORM.ANDROID),
            IPAddress: userGeoInfo.ip,
            authorizationCode: codeResponse.code,
            referrerId: sessionStorage.getItem(LOCAL_STORAGE.REFERRAL_ID) || null,
            signupAttr: signUpParams || null,
            country: userGeoInfo.country,
            signUpIPCountry: userGeoInfo.country,
            redirectUri: 'postmessage',
          })

      if (authResp.type === 'login') {
        await login(authResp.accessToken, true)
        hideLoader()
      } else {
        hideLoader()
        localStorage.setItem(LOCAL_STORAGE.JWT_TOKEN, authResp.accessToken)
        if (!codeResponse.scope.includes('https://www.googleapis.com/auth/gmail.readonly')) {
          const decodedToken = decodeToken(authResp.accessToken)

          const email = (decodedToken as { email: string }).email

          setTimeout(
            () =>
              getAuthLinkWithScopes(email, '').then(
                (authLink) => (window.location.href = authLink as unknown as string)
              ),
            100
          )
        } else {
          navigate('/platform-select')
        }
      }
    } catch (err: any) {
      hideLoader()
      handleError(err)
    }
  }

  const handleAuthorizationWeb = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      const isBanned = false // You should pass this from the component if needed
      await handleGoogleLoginSuccess(codeResponse, isBanned, false)
    },
    onError: () => new Error('No authorization code received'),
    scope:
      'https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email',
    flow: 'auth-code',
  })

  const handleSignUpWeb = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      const isBanned = false // You should pass this from the component if needed
      await handleGoogleLoginSuccess(codeResponse, isBanned, true)
    },
    onError: () => new Error('No authorization code received'),
    scope:
      'https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/gmail.readonly',
    flow: 'auth-code',
  })

  return {
    handleAuthorization: window.ReactNativeWebView
      ? handleAuthorizationNative
      : handleAuthorizationWeb,
    handleSignup: window.ReactNativeWebView ? handleSignupNative : handleSignUpWeb,
  }
}

export default useAuthorization
